/**
 * Génère un menu journalier cohérent, priorisé et équilibré sans jamais dépasser la cible calorique.
 *
 * Règles :
 * - Lunch/Dinner → entrée + plat + dessert si dispo
 * - Breakfast → uniquement breakfast
 * - Snacks → ajoutés uniquement si des calories restent
 * - Chaque recette = 1 portion entière (pas de fraction)
 * - Optimisation "sac à dos" pour se rapprocher au maximum de la cible sans dépasser (+1 % tolérance)
 */
export function generateMenu(recipes, userConstraints, weekUsed = null) {
  if (!Array.isArray(recipes) || !recipes.length)
    return { menu: {}, totals: { kcal: 0, protein: 0, carbs: 0, fat: 0 } };

  const {
    targetCalories = 2000,
    takenMealsObjectives = ["breakfast", "lunch", "dinner", "snacks"],
    allergies = [],
    intolerances = [],
    bannedIngredients = [],
    maxPrepTime = null, // Dégage
    skillLevel = null, // Dégage
    availableEquipment = [], // Dégage
  } = userConstraints;

  // Helpers
  const normalize = (str) => String(str || "").trim().toLowerCase().replace(/s$/, "");
  const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];
  const getKcal = (r) => (r?.totals?.kcal ?? 0) / (r.servings && r.servings > 0 ? r.servings : 1);
  const getMacros = (r) => {
    const s = r.servings && r.servings > 0 ? r.servings : 1;
    const t = r.totals || {};
    return {
      kcal: (t.kcal ?? 0) / s,
      protein: (t.protein ?? 0) / s,
      carbs: (t.carbs ?? 0) / s,
      fat: (t.fat ?? 0) / s,
    };
  };

  // Normalisation des repas
  const normalizedMeals = takenMealsObjectives.map(normalize);

  // Filtrage des recettes selon contraintes
  const filteredRecipes = recipes.filter((r) => {
    const ingredients = (r.ingredients || []).map((i) => i.name?.toLowerCase?.() || "");
    const safe = (list, fn) => list.every((x) => fn(x));
    return (
      safe(allergies, (a) => !ingredients.includes(a.toLowerCase())) &&
      safe(intolerances, (i) => !ingredients.includes(i.toLowerCase())) &&
      safe(bannedIngredients, (b) => !ingredients.includes(b.toLowerCase())) &&
      (!maxPrepTime || (r.prepTime ?? 0) <= maxPrepTime) &&
      (!skillLevel || (r.skillLevel ?? "intermediate") === skillLevel) &&
      (!availableEquipment.length ||
        availableEquipment.every((eq) => (r.equipment || []).includes(eq)))
    );
  });

  if (!filteredRecipes.length)
    return { menu: {}, totals: { kcal: 0, protein: 0, carbs: 0, fat: 0 } };

  // Groupes par catégorie
  const byCategory = {
    starter: filteredRecipes.filter((r) => normalize(r.category) === "starter"),
    meal: filteredRecipes.filter((r) => normalize(r.category) === "meal"),
    dessert: filteredRecipes.filter((r) => normalize(r.category) === "dessert"),
    breakfast: filteredRecipes.filter((r) => normalize(r.category) === "breakfast"),
    snack: filteredRecipes.filter((r) => normalize(r.category) === "snack"),
  };

  // Construction du menu
  const menu = {};
  // Si un pool hebdomadaire est fourni (diversité), on part de ses ids déjà utilisés
  const used = weekUsed ? new Set(weekUsed) : new Set();

  const addRecipe = (cat, type) => {
    // try to pick an unused recipe first, but if none left in the category
    // fall back to the full category (allow reuse) so the meal still has an entry
    const allInCat = byCategory[cat] || [];
    const unused = allInCat.filter((r) => !used.has(r.id));
    const pool = unused.length ? unused : allInCat;
    if (!pool.length) return null;
    const chosen = pickRandom(pool);
    menu[type].push(chosen);
    // mark as used only when we actually consumed an unused recipe
    if (unused.length) used.add(chosen.id);
  };

  // Lunch & Dinner (entrée + plat + dessert)
  for (const type of ["lunch", "dinner"]) {
    if (!normalizedMeals.includes(type)) continue;
    menu[type] = [];
    ["starter", "meal", "dessert"].forEach((cat) => addRecipe(cat, type));
  }

  // Breakfast
  if (normalizedMeals.includes("breakfast") && byCategory.breakfast.length) {
    menu.breakfast = [];
    addRecipe("breakfast", "breakfast");
  }

  // Calcul des totaux initiaux
  const totals = { kcal: 0, protein: 0, carbs: 0, fat: 0 };
  for (const r of Object.values(menu).flat()) {
    const t = getMacros(r);
    totals.kcal += t.kcal;
    totals.protein += t.protein;
    totals.carbs += t.carbs;
    totals.fat += t.fat;
  }

  // ⚡ Sélection des collations pour se rapprocher au maximum de la cible
  // Option B : les collations ignorent le plafond et sont ajoutées jusqu'à
  // ce que le total soit le plus proche possible de la cible, même si on dépasse.
  if (byCategory.snack.length) {
    // Trie les collations par kcal croissant pour remplir au plus juste
    const snacks = [...byCategory.snack].sort((a, b) => getKcal(a) - getKcal(b));

    let remainingKcal = targetCalories - totals.kcal;

    // Passe 1 : ajouter des collations tant qu'on est en dessous de la cible
    // On prend la collation dont les kcal sont les plus proches du manque restant
    let safety = 0;
    while (remainingKcal > 0 && snacks.length && safety < 20) {
      safety++;

      // Cherche la collation qui se rapproche le plus de remainingKcal sans dépasser
      const best = snacks.reduce((prev, cur) => {
        const prevKcal = getKcal(prev);
        const curKcal = getKcal(cur);
        // Préfère celle qui comble le mieux sans dépasser ; si toutes dépassent, prend la plus petite
        const prevDiff = remainingKcal - prevKcal;
        const curDiff = remainingKcal - curKcal;
        if (prevDiff >= 0 && curDiff >= 0) return prevDiff < curDiff ? prev : cur;
        if (prevDiff >= 0) return prev;
        if (curDiff >= 0) return cur;
        return Math.abs(prevDiff) < Math.abs(curDiff) ? prev : cur;
      });

      const bestKcal = getKcal(best);

      // Si on est déjà très proche (< 10 kcal d'écart) on s'arrête
      if (remainingKcal < 10) break;

      const t = getMacros(best);
      menu.snack = menu.snack || [];
      menu.snack.push({ ...best });
      totals.kcal += t.kcal;
      totals.protein += t.protein;
      totals.carbs += t.carbs;
      totals.fat += t.fat;
      used.add(best.id);

      remainingKcal = targetCalories - totals.kcal;

      // Si ajouter cette collation nous a fait dépasser, on s'arrête
      if (bestKcal > remainingKcal + bestKcal) break;
      // Retire la collation utilisée du pool pour éviter répétition infinie
      snacks.splice(snacks.indexOf(best), 1);
      if (!snacks.length) break;
    }
  }

  return { menu, totals, usedIds: used };
}

/**
 * Génère un plan de menus pour une semaine (7 jours) en réutilisant generateMenu.
 * options:
 *  - diverse: boolean (false) : si true, on "intervertit" les menus un jour sur 3
 *    pour éviter d'avoir les mêmes menus collés (ex: swap jour 3<->4, 6<->7).
 *
 * Retourne { daily: [...7 elements { menu, totals }], weeklyTotals: { kcal, protein, carbs, fat } }
 */
export function generateWeeklyMenu(recipes, userConstraints = {}, options = { diverse: false }) {
  if (!Array.isArray(recipes) || !recipes.length) {
    return { daily: [], weeklyTotals: { kcal: 0, protein: 0, carbs: 0, fat: 0 } };
  }

  const days = 7;
  const daily = [];

  // sameLunchDinnerDays : tableau d'indices 0-6 pour lesquels dinner = lunch
  const sameDays = Array.isArray(options.sameLunchDinnerDays) ? options.sameLunchDinnerDays : [];

  // Si diversité activée : pool de recettes déjà utilisées partagé entre les 7 jours
  const weekUsed = (options && options.diverse) ? new Set() : null;

  for (let d = 0; d < days; d++) {
    const dayResult = generateMenu(recipes, userConstraints, weekUsed);
    const menu = dayResult.menu || {};
    // Enregistrer les recettes utilisées ce jour dans le pool hebdomadaire
    if (weekUsed && dayResult.usedIds) {
      for (const id of dayResult.usedIds) weekUsed.add(id);
    }

    // Si ce jour est sélectionné pour "même plat midi et soir", on copie lunch dans dinner
    if (sameDays.includes(d) && menu.lunch && menu.lunch.length) {
      menu.dinner = menu.lunch.map((r) => ({ ...r }));
    }

    // Totaux recalculés depuis ce qui est réellement affiché dans le menu
    const recalc = { kcal: 0, protein: 0, carbs: 0, fat: 0 };
    for (const mealRecipes of Object.values(menu)) {
      for (const r of mealRecipes) {
        const s = r.servings && r.servings > 0 ? r.servings : 1;
        const t = r.totals || {};
        recalc.kcal += (t.kcal ?? 0) / s;
        recalc.protein += (t.protein ?? 0) / s;
        recalc.carbs += (t.carbs ?? 0) / s;
        recalc.fat += (t.fat ?? 0) / s;
      }
    }

    daily.push({ menu, totals: recalc });
  }

  // Totaux hebdomadaires
  const weeklyTotals = daily.reduce(
    (acc, d) => {
      acc.kcal += Number(d.totals.kcal || 0);
      acc.protein += Number(d.totals.protein || 0);
      acc.carbs += Number(d.totals.carbs || 0);
      acc.fat += Number(d.totals.fat || 0);
      return acc;
    },
    { kcal: 0, protein: 0, carbs: 0, fat: 0 }
  );

  return { daily, weeklyTotals };
}
